import React from 'react';
import { Outlet } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { AdminNavHost } from '../nav/AdminNavHost';
import { useSidebar } from '../../context/SidebarContext';
import { PageLayout } from './PageLayout';

interface AdminLayoutProps {
  children?: React.ReactNode;
  /**
   * Whether to wrap content in PageLayout
   * @default true
   */
  usePageLayout?: boolean;
  /**
   * PageLayout maxWidth prop (only used if usePageLayout is true)
   * @default '7xl'
   */
  pageMaxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full';
  /**
   * PageLayout padding prop (only used if usePageLayout is true)
   * @default 'md'
   */
  pagePadding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Skip top padding in PageLayout (useful when first child is a header)
   * @default false
   */
  skipTopPadding?: boolean;
}

function AdminLayoutContent({ 
  children, 
  usePageLayout = true,
  pageMaxWidth = 'full',
  pagePadding = 'md',
  skipTopPadding = false,
}: AdminLayoutProps) {
  const { isOpen, toggle } = useSidebar();

  return (
    <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter'] flex">
      <AdminNavHost />
      
      {/* Mobile header bar with hamburger menu */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-[#221F1F] border-b border-[#504A4A] z-50 flex items-center px-4">
        <button
          onClick={toggle}
          className="w-10 h-10 flex items-center justify-center text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded-lg transition-colors"
          aria-label="Toggle sidebar"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center space-x-2 ml-3">
          <img 
            src="/rollops-logo.png" 
            alt="RollOps Logo" 
            width={24} 
            height={24} 
            className="w-6 h-6" 
          />
          <span className="text-lg font-bold text-[#EBFFEE]">
            Roll<span className="text-rollops-accent">O</span>ps
          </span>
          <span className="text-xs text-[#A9A3A3]">Backend</span>
        </div>
      </header>
      
      {/* Backdrop overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={toggle}
          aria-hidden="true"
        />
      )}
      
      <main 
        className={`
          flex-1 transition-all duration-300 ease-in-out min-w-0 relative
          ${isOpen ? 'md:ml-64' : 'md:ml-16'} 
          ml-0 w-full
          pt-14 md:pt-0
        `}
        style={{
          '--sidebar-width': isOpen ? '256px' : '64px',
        } as React.CSSProperties & { '--sidebar-width': string }}
      >
        {usePageLayout ? (
          <PageLayout maxWidth={pageMaxWidth} padding={pagePadding} skipTopPadding={skipTopPadding}>
            {children}
          </PageLayout>
        ) : (
          <div className="w-full max-w-full min-w-0 overflow-x-hidden">
            {children}
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * AdminLayout component for admin/backend pages
 * 
 * Supports two modes:
 * - Nested routing: Uses <Outlet /> when no children provided (layout stays mounted)
 * - Direct children: Uses children prop when provided (legacy support)
 */
export const AdminLayout: React.FC<AdminLayoutProps> = ({ children, ...pageLayoutProps }) => {
  // Use Outlet for nested routing (prevents layout remount on navigation)
  // Fall back to children for backwards compatibility
  const content = children ?? <Outlet />;
  return <AdminLayoutContent {...pageLayoutProps}>{content}</AdminLayoutContent>;
};

